import { ShoppingListIngredientDetail } from "@/clients/models/ShoppingListIngredientDetail";
import { ShoppingListUpdateRequest } from "@/clients/models/ShoppingListUpdateRequest";
import { ShoppingListService } from "@/clients/services/ShoppingListService";
import { QueryKeys } from "@/constants/query-keys";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Hook to update shopping list with instant UI feedback
 * Updates cache immediately, then syncs with server in the background
 */
export const useUpdateShoppingList = () => {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isError, error } = useMutation({
    mutationFn: async (request: ShoppingListUpdateRequest) => {
      const user = guardGetUserFromLocalStorage();
      return await ShoppingListService.updateShoppingListTickedStatusApiV1ShoppingListPut(
        user.id,
        request,
      );
    },
    onMutate: (request: ShoppingListUpdateRequest) => {
      const currentData = queryClient.getQueryData<
        ShoppingListIngredientDetail[]
      >([QueryKeys.SHOPPING_LIST]);

      if (currentData) {
        const tickedSet = new Set(request.tickedIngredientIds);
        queryClient.setQueryData<ShoppingListIngredientDetail[]>(
          [QueryKeys.SHOPPING_LIST],
          currentData.map((item) => ({
            ...item,
            isTicked: tickedSet.has(item.ingredientId),
          })),
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.SHOPPING_LIST] });
    },
  });

  return {
    updateShoppingList: mutate,
    updateShoppingListAsync: mutateAsync,
    isUpdating: isPending,
    isError,
    error,
  };
};
